import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Button, Icon, Intent, Spinner } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import FormDrawer from 'components/FormDrawer';
import AddEquipmentModal from 'components/Equipment/AddEquipmentModal';

// "Add equipment" on a work order: the client's equipment at the work order's
// address, searchable, each added with one click (and taken off again); new
// equipment can be created here and goes straight onto the work order. The
// panel sets up its own search when it opens, so a caller only passes the work
// order (`workDetail`). The footer keeps a tally of what this visit added.
// Styles: styles/line-item-picker.scss (pick-, shared with Add labor/material).

// Wait this long after the last keystroke before searching.
const SEARCH_DELAY_MS = 350;
const PER_PAGE = 10;

export const equipmentTitle = (attributes) =>
  attributes.kind || [attributes.make, attributes.eq_model].filter(Boolean).join(' ') || 'Equipment';

const thumbnail = (attributes) => {
  const image = (attributes.images || []).find((item) => item && item.asset && item.asset.thumb && item.asset.thumb.url);
  return image ? image.asset.thumb.url : null;
};

// The record's client and location are plain rows; the pickers in the New
// equipment panel show records shaped like the API's ({ id, attributes }).
const asRecord = (row) => (row ? (row.attributes ? row : { id: row.id, attributes: { ...row } }) : null);

@inject('equipmentStore', 'viewStore', 'workReportStore') @observer
class AddEquipmentForWorkReportModal extends Component {
  @observable retired = false;
  @observable addingId = null;
  @observable removing = null;       // { id, name, servicedId } waiting on the prompt
  @observable removingId = null;
  @observable tally = [];            // names added this visit
  @observable isNewOpen = false;

  searchInput = React.createRef();

  componentWillUnmount() {
    clearTimeout(this.searchTimer);
  }

  get search() {
    return this.props.viewStore.equipmentSearchWorkReport;
  }

  get workOrder() {
    return this.props.workDetail || this.props.workReportStore.workReport.object;
  }

  // Equipment on the work order, by equipment id -> its serviced-equipment id.
  get onWorkOrder() {
    const equipments = (this.workOrder && this.workOrder.attributes.equipments) || [];
    return equipments.reduce((map, item) => ({ ...map, [item.id]: item.serviced_equipment_id }), {});
  }

  // Each opening starts on this client's active equipment at this address.
  handleOpened = () => {
    const attributes = this.workOrder.attributes;
    const client = attributes.client || {};
    this.tally = [];
    this.retired = false;
    this.search.term = '*';
    this.search.page = 1;
    this.search.per_page = PER_PAGE;
    this.search.where.client_id = client.id || null;
    this.search.where.location_id = attributes.location_id || null;
    this.search.where.active = true;
    this.props.equipmentStore.loadEquipmentsForWorkReports();
    if (this.searchInput.current) this.searchInput.current.focus();
  };

  close = () => {
    clearTimeout(this.searchTimer);
    this.props.onClose();
  };

  runSearch = (page = 1) => {
    clearTimeout(this.searchTimer);
    if (!this.search.term) this.search.term = '*';
    this.search.page = page;
    this.props.equipmentStore.loadEquipmentsForWorkReports();
  };

  onSearchChange = (event) => {
    this.search.term = event.target.value;
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.runSearch(), SEARCH_DELAY_MS);
  };

  onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.runSearch();
    }
  };

  clearSearch = () => {
    this.search.term = '';
    this.runSearch();
    if (this.searchInput.current) this.searchInput.current.focus();
  };

  showRetired(retired) {
    if (this.retired === retired) return;
    this.retired = retired;
    this.search.where.active = !retired;
    this.runSearch();
  }

  add(id, name) {
    if (this.addingId) return;
    this.addingId = id;
    this.props.equipmentStore.create_serviced_equipment(id, this.workOrder.id)
      .then(() => {
        this.addingId = null;
        this.tally.push(name);
      })
      .catch(() => {
        this.addingId = null;
        Toast.show({ message: `Couldn't add ${name} to the work order. Try again.`, intent: Intent.DANGER });
      });
  }

  confirmRemove = () => {
    const { id, name, servicedId } = this.removing;
    this.removing = null;
    this.removingId = id;
    this.props.equipmentStore.deleteServicedEquipment(servicedId, this.workOrder.id)
      .then(() => {
        this.removingId = null;
        this.props.equipmentStore.loadEquipmentsForWorkReports();
        Toast.show({ message: `${name} taken off the work order.`, intent: Intent.SUCCESS });
      })
      .catch(() => {
        this.removingId = null;
        Toast.show({ message: `Couldn't take ${name} off the work order. Try again.`, intent: Intent.DANGER });
      });
  };

  // New equipment starts on this work order's client and address.
  openNew = () => {
    const attributes = this.workOrder.attributes;
    const object = this.props.equipmentStore.persistEquipment.object;
    if (attributes.client) {
      object.client_id = attributes.client.id;
      object.client = asRecord(attributes.client);
    }
    if (attributes.location_id && attributes.location) {
      object.location_id = attributes.location_id;
      object.location = asRecord(attributes.location);
    }
    this.isNewOpen = true;
  };

  // Created here, so it's for this work order: add it straight away.
  onCreated = (equipment) => {
    this.isNewOpen = false;
    if (equipment && equipment.id) {
      this.add(equipment.id, equipmentTitle(equipment.attributes || {}));
    }
  };

  renderRow(equipment) {
    const attributes = equipment.attributes;
    const name = equipmentTitle(attributes);
    const servicedId = this.onWorkOrder[equipment.id];
    const added = Boolean(servicedId);
    const image = thumbnail(attributes);
    const facts = [['Make', attributes.make], ['Model', attributes.eq_model], ['Serial', attributes.serial_number]].filter(([, value]) => value);
    return (
      <li key={equipment.id} className={`pick-row pick-row--equipment${added ? ' is-on' : ''}`}>
        {image
          ? <img className="pick-thumb" src={image} alt="" />
          : <span className="pick-thumb pick-thumb--icon" aria-hidden="true"><Icon icon="wrench" iconSize={16} /></span>}
        <div className="pick-row__main">
          <span className="pick-row__name">
            {name}
            {this.retired && <span className="pick-pill">Retired</span>}
          </span>
          {facts.length > 0 && (
            <span className="pick-facts">
              {facts.map(([label, value]) => (
                <span key={label}><span className="pick-facts__label">{label}</span> {value}</span>
              ))}
            </span>
          )}
          {attributes.eq_location && <span className="pick-row__description">{attributes.eq_location}</span>}
        </div>
        <div className="pick-row__side">
          {added ? (
            <>
              <span className="pick-on"><Icon icon="tick" iconSize={12} /> On this work order</span>
              <button
                type="button"
                className="pick-text-button"
                disabled={this.removingId === equipment.id}
                onClick={() => { this.removing = { id: equipment.id, name, servicedId }; }}
              >
                {this.removingId === equipment.id ? 'Taking off…' : 'Take off'}
              </button>
            </>
          ) : !this.retired && (
            <Button
              className="pick-equipment-add"
              icon={this.addingId === equipment.id ? <Spinner size={14} /> : 'plus'}
              text="Add"
              aria-label={`Add ${name} to the work order`}
              disabled={Boolean(this.addingId) && this.addingId !== equipment.id}
              onClick={() => this.add(equipment.id, name)}
            />
          )}
        </div>
      </li>
    );
  }

  renderResults(term) {
    const { objects, isLoading, isError, meta } = this.props.equipmentStore.equipments;
    if (isLoading) {
      return (
        <ul className="pick-list" aria-busy="true" aria-label="Loading">
          {[0, 1, 2].map((index) => (
            <li key={index} className="pick-row pick-row--equipment pick-row--bone" aria-hidden="true">
              <span className="dl-bone pick-thumb" />
              <div className="pick-row__main">
                <span className="dl-bone" style={{ width: '40%', height: 14 }} />
                <span className="dl-bone" style={{ width: '65%', height: 10, marginTop: 8 }} />
              </div>
              <span className="dl-bone" style={{ width: 72, height: 32, borderRadius: 8 }} />
            </li>
          ))}
        </ul>
      );
    }
    if (isError) {
      return (
        <div className="pick-empty" role="alert">
          <p className="pick-empty__title">The equipment couldn't be loaded.</p>
          <p className="pick-empty__hint">Check your connection, then try again.</p>
          <Button icon="refresh" text="Try again" onClick={() => this.runSearch(this.search.page || 1)} />
        </div>
      );
    }
    if (!objects || objects.length === 0) {
      if (term) {
        return (
          <div className="pick-empty" role="status">
            <p className="pick-empty__title">No {this.retired ? 'retired ' : ''}equipment matches “{term}”.</p>
            <p className="pick-empty__hint">Try the make, model or serial number{this.retired ? '' : ', or add it as new equipment'}.</p>
            <div className="pick-empty__actions">
              {!this.retired && <Button icon="add" intent={Intent.PRIMARY} text="New equipment" onClick={this.openNew} />}
              <Button minimal text="Clear search" onClick={this.clearSearch} />
            </div>
          </div>
        );
      }
      return this.retired ? (
        <div className="pick-empty" role="status">
          <p className="pick-empty__title">No retired equipment here.</p>
          <p className="pick-empty__hint">Equipment taken out of service at this address shows up here.</p>
        </div>
      ) : (
        <div className="pick-empty" role="status">
          <p className="pick-empty__title">No equipment on file at this address yet.</p>
          <p className="pick-empty__hint">Add the unit you’re working on, like a furnace or water heater. It goes straight onto this work order.</p>
          <div className="pick-empty__actions">
            <Button icon="add" intent={Intent.PRIMARY} text="New equipment" onClick={this.openNew} />
          </div>
        </div>
      );
    }
    const page = (meta && meta.page) || 1;
    const pages = (meta && meta.total_pages) || 1;
    return (
      <>
        <ul className="pick-list" aria-label={this.retired ? 'Retired equipment' : 'Equipment'}>
          {objects.map((equipment) => this.renderRow(equipment))}
        </ul>
        {pages > 1 && (
          <nav className="pick-pages" aria-label="Pages">
            <span className="pick-pages__info">Page {page} of {pages}</span>
            <Button minimal icon="chevron-left" text="Previous" disabled={page <= 1} onClick={() => this.runSearch(page - 1)} />
            <Button minimal rightIcon="chevron-right" text="Next" disabled={page >= pages} onClick={() => this.runSearch(page + 1)} />
          </nav>
        )}
      </>
    );
  }

  render() {
    const { isOpen } = this.props;
    const workOrder = this.workOrder;
    if (!workOrder) return null;
    const attributes = workOrder.attributes;
    const term = this.search.term && this.search.term !== '*' ? this.search.term : '';
    const address = (attributes.location && attributes.location.full_address) || (attributes.current_location && attributes.current_location.full_address);
    const total = this.props.equipmentStore.equipments.meta && this.props.equipmentStore.equipments.meta.total_records;
    const added = this.tally.length > 0;
    return (
      <>
        <FormDrawer
          title="Add equipment"
          icon="wrench"
          tone="field"
          size="min(720px, 100vw)"
          className="pick"
          isOpen={isOpen}
          onOpened={this.handleOpened}
          onClose={this.close}
          footer={
            <div className="pick-tally">
              {added ? (
                <div className="pick-tally__added" aria-live="polite">
                  <span className="pick-tally__count">{this.tally.length} added</span>
                  <ul className="pick-tally__chips">
                    {this.tally.map((name, index) => <li key={`${name}-${index}`} className="pick-chip">{name}</li>)}
                  </ul>
                </div>
              ) : (
                <span className="pick-tally__hint">Add each unit you work on, so its history stays with it.</span>
              )}
              <div className="pick-tally__actions">
                <Button text={added ? 'Done' : 'Close'} intent={added ? Intent.PRIMARY : Intent.NONE} onClick={this.close} />
              </div>
            </div>
          }
        >
          {(attributes.client || address) && (
            <p className="pick-context">
              <Icon icon="map-marker" iconSize={12} />
              {attributes.client && <strong>{attributes.client.name}</strong>}
              {address && <span>{address}</span>}
            </p>
          )}
          <div className="pick-search">
            <label className="sr-only" htmlFor="pick-equipment-search">Find equipment</label>
            <div className="pick-search__field">
              <Icon icon="search" iconSize={16} className="pick-search__icon" />
              <input
                id="pick-equipment-search"
                ref={this.searchInput}
                type="search"
                autoComplete="off"
                placeholder="Search by make, model, serial number or description"
                value={term}
                onChange={this.onSearchChange}
                onKeyDown={this.onSearchKeyDown}
              />
              {term && (
                <button type="button" className="pick-search__clear" aria-label="Clear search" onClick={this.clearSearch}>
                  <Icon icon="cross" iconSize={14} />
                </button>
              )}
            </div>
            <Button className="pick-new" icon="add" text="New equipment" onClick={this.openNew} />
          </div>
          <div className="pick-switch" role="group" aria-label="Show">
            <button type="button" className={`pick-switch__option${this.retired ? '' : ' is-on'}`} aria-pressed={!this.retired} onClick={() => this.showRetired(false)}>
              In service{!this.retired && total ? <span className="pick-switch__count">{total}</span> : null}
            </button>
            <button type="button" className={`pick-switch__option${this.retired ? ' is-on' : ''}`} aria-pressed={this.retired} onClick={() => this.showRetired(true)}>
              Retired{this.retired && total ? <span className="pick-switch__count">{total}</span> : null}
            </button>
          </div>
          {this.renderResults(term)}
        </FormDrawer>
        <AddEquipmentModal
          isOpen={this.isNewOpen}
          onClose={() => { this.isNewOpen = false; }}
          onSuccess={this.onCreated}
        />
        <Alert
          isOpen={Boolean(this.removing)}
          icon="cross"
          intent={Intent.DANGER}
          cancelButtonText="Keep it"
          confirmButtonText="Take it off"
          onCancel={() => { this.removing = null; }}
          onConfirm={this.confirmRemove}
        >
          <strong>Take {(this.removing && this.removing.name) || 'this equipment'} off the work order?</strong>
          <p>It stays on the client’s equipment list.</p>
        </Alert>
      </>
    );
  }
}

export default AddEquipmentForWorkReportModal;
